// =============================================================================
// MainApp — top-level component for the public site and admin app.
// =============================================================================
// Hash-based routing (no router library).
//
// Public:
//   #                  → public homepage (CalendarHero + calendar + list view)
//   #event/<id>        → per-event detail page (Register modal lives inside)
//   #e/<slug>          → per-event detail page by persistent slug
//   #login             → unified login page (UIF / AGC / AMP)
//   #amp               → AMP marketing landing page
//
// Admin (Round 4 sidebar redesign):
//   #admin             → legacy alias; bounces to #login (if anon) or
//                        #admin/dashboard (if signed in)
//   #admin/dashboard          → AdminDashboard (default landing after login)
//   #admin/events             → AdminEventsPanel (table list)
//   #admin/events/new         → EventForm (create)
//   #admin/events/bulk        → CsvBulkUpload
//   #admin/events/edit/:id    → EventForm (edit)
//   #admin/registrations      → AdminRegistrations
//   #admin/series             → AdminSeriesPanel
//   #admin/presenters         → AdminPresentersPanel
//   #admin/locations          → AdminLocationsPanel
//   #admin/resources          → AdminResourcesPanel (rooms + staff, Task #3)
//   #admin/bookings           → AdminBookingsPanel (shared room calendar)
//
// Legacy admin URLs (kept working so older bookmarks/QR codes don't 404):
//   #admin-dashboard            → #admin/dashboard
//   #admin-event-create         → #admin/events/new
//   #admin-event-bulk           → #admin/events/bulk
//   #admin-event-edit/<id>      → #admin/events/edit/<id>
//
// AGC + AMP:
//   #agc               → AGC shared room calendar (full UI)
//   #amp-events        → AMP read-only events placeholder
// =============================================================================

import { useEffect, useState } from 'react';
import { useAuth } from './auth';
import { useFarmerAuth } from './farmerAuth';
import { CalendarHero } from './components/CalendarHero';
import { LoginPage } from './components/LoginPage';
import { AgcCalendar } from './components/AgcCalendar';
import { AmpCalendarPlaceholder } from './components/AmpCalendarPlaceholder';
import { AmpFunding } from './components/AmpFunding';
import { AmpAlert } from './components/AmpAlert';
import { PublicCalendar } from './components/PublicCalendar';
import { SubscribeBox } from './components/SubscribeBox';
import { EventDetailPage } from './components/EventDetailPage';
import { TagFilter } from './components/TagFilter';
import { EventForm } from './components/EventForm';
import { CsvBulkUpload } from './components/CsvBulkUpload';
import { QrCodeModal } from './components/QrCodeModal';
import { QrCode } from 'lucide-react';
import { AdminLayout, type AdminSection } from './components/AdminLayout';
import { AdminDashboard } from './components/AdminDashboard';
import { AdminEventsPanel } from './components/AdminEventsPanel';
import { AdminRegistrations } from './components/AdminRegistrations';
import { AdminSeriesPanel } from './components/AdminSeriesPanel';
import { AdminPresentersPanel } from './components/AdminPresentersPanel';
import { AdminLocationsPanel } from './components/AdminLocationsPanel';
import { AdminResourcesPanel } from './components/AdminResourcesPanel';
import { AdminTagsPanel } from './components/AdminTagsPanel';
import { AdminReportsPanel } from './components/AdminReportsPanel';
import { AdminSubscribersPanel } from './components/AdminSubscribersPanel';
import { AdminPagesPanel } from './components/AdminPagesPanel';
import { AdminFarmersPanel } from './components/AdminFarmersPanel';
import { AdminFarmsPanel } from './components/AdminFarmsPanel';
import { AdminDocumentsPanel } from './components/AdminDocumentsPanel';
import { FarmerDocumentsPage } from './components/FarmerDocumentsPage';
import { AdminEnvelopesPanel } from './components/AdminEnvelopesPanel';
import { AdminAgreementsPanel } from './components/AdminAgreementsPanel';
import { FarmerSignaturesTab } from './components/FarmerSignaturesTab';
import { ExternalSignPage } from './pages/ExternalSignPage';
import { PublicAttendeeInfoPanel } from './components/PublicAttendeeInfoPanel';
import { FarmerOnboardPage } from './components/FarmerOnboardPage';
import { JoinMeetingPage } from './components/JoinMeetingPage';
import { SubscribeConfirmPage } from './components/SubscribeConfirmPage';
import { UnsubscribePage } from './components/UnsubscribePage';
import { FarmerSignInPage } from './components/FarmerSignInPage';
import { FarmerVerifyPage } from './components/FarmerVerifyPage';
import { FarmerResetPage } from './components/FarmerResetPage';
import { FarmerHomePage } from './components/FarmerHomePage';
import { FarmerRegistrationsPage } from './components/FarmerRegistrationsPage';
import { AdminBookingsPanel } from './components/AdminBookingsPanel';
import { AdminBudgetList } from './components/AdminBudgetList';
import { AdminBudgetPanel } from './components/AdminBudgetPanel';
import { ConfirmPage } from './components/ConfirmPage';
import { SignInPage } from './components/SignInPage';
import { PasswordResetPage } from './components/PasswordResetPage';
import { AdminProgramsPanel } from './components/AdminProgramsPanel';
import { AdminEnrollmentsPanel } from './components/AdminEnrollmentsPanel';
import { FarmerEnrollmentTab } from './components/FarmerEnrollmentTab';
import { AdminTracksPanel } from './components/AdminTracksPanel';
import { AdminMilestoneDefsPanel } from './components/AdminMilestoneDefsPanel';
import { AdminPracticesPanel } from './components/AdminPracticesPanel';
import { AdminCpa52Panel } from './components/AdminCpa52Panel';
import { AdminEnrollmentRecordExternalPanel } from './components/AdminEnrollmentRecordExternalPanel';
import { AdminEnrollmentRecordExternalBatchPanel } from './components/AdminEnrollmentRecordExternalBatchPanel';
import { FarmerTracksTab } from './components/FarmerTracksTab';
import { AdminEventAttendanceReportPanel } from './components/AdminEventAttendanceReportPanel';

type Route =
  | { name: 'home' }
  | { name: 'login' }
  | { name: 'admin-section'; section: AdminSection }
  | { name: 'admin-event-create' }
  | { name: 'admin-event-bulk' }
  | { name: 'admin-event-edit'; eventId: number }
  | { name: 'admin-event-attendance-report'; eventId: number }
  | { name: 'admin-budget'; calendarEntryId: number }
  | { name: 'admin-farm-detail'; farmId: number }
  | { name: 'admin-document-detail'; documentId: string }
  | { name: 'admin-envelope-detail'; envelopeId: string }
  | { name: 'admin-agreement-detail'; agreementId: string }
  | { name: 'admin-enrollment-detail'; enrollmentId: string }
  | { name: 'admin-enrollment-record-external'; enrollmentId: string }
  | { name: 'admin-enrollment-record-external-batch' }
  | { name: 'admin-track-detail'; trackPlanId: string }
  | { name: 'farmer-documents' }
  | { name: 'farmer-signatures' }
  | { name: 'farmer-enrollment' }
  | { name: 'farmer-tracks' }
  | { name: 'external-sign'; linkToken: string }
  | { name: 'attendee-info'; token: string }
  | { name: 'agc' }
  | { name: 'amp-events' }
  | { name: 'amp' }
  | { name: 'event-detail'; eventId: number }
  | { name: 'event-detail-slug'; slug: string }
  | { name: 'confirm'; token: string }
  | { name: 'signin'; eventId: number; dayIndex: number }
  | { name: 'password-reset'; token: string }
  | { name: 'subscribe-confirm'; token: string }
  | { name: 'unsubscribe'; token: string }
  | { name: 'farmer-signin' }
  | { name: 'farmer-verify'; token: string }
  | { name: 'farmer-reset'; token: string }
  | { name: 'farmer-home' }
  | { name: 'farmer-registrations' }
  | { name: 'admin-pages-edit'; pageId: number | 'new' }
  | { name: 'join-meeting'; token: string }
  | { name: 'farmer-onboard'; token: string };

function getRoute(): Route {
  if (typeof window === 'undefined') return { name: 'home' };
  const h = window.location.hash;

  // Public + auth routes
  if (h === '#login') return { name: 'login' };
  if (h === '#agc') return { name: 'agc' };
  if (h === '#amp-events') return { name: 'amp-events' };
  if (h === '#amp') return { name: 'amp' };

  // Legacy admin URLs — auto-redirect for back-compat
  if (h === '#admin')             { window.location.hash = '#admin/dashboard'; return { name: 'admin-section', section: 'dashboard' }; }
  if (h === '#admin-dashboard')   { window.location.hash = '#admin/dashboard'; return { name: 'admin-section', section: 'dashboard' }; }
  if (h === '#admin-event-create') { window.location.hash = '#admin/events/new'; return { name: 'admin-event-create' }; }
  if (h === '#admin-event-bulk')   { window.location.hash = '#admin/events/bulk'; return { name: 'admin-event-bulk' }; }
  const legacyEdit = h.match(/^#admin-event-edit\/(\d+)$/);
  if (legacyEdit) {
    window.location.hash = `#admin/events/edit/${legacyEdit[1]}`;
    return { name: 'admin-event-edit', eventId: Number(legacyEdit[1]) };
  }

  // New admin routes
  if (h === '#admin/dashboard')     return { name: 'admin-section', section: 'dashboard' };
  if (h === '#admin/events')        return { name: 'admin-section', section: 'events' };
  if (h === '#admin/events/new')    return { name: 'admin-event-create' };
  if (h === '#admin/events/bulk')   return { name: 'admin-event-bulk' };
  const editMatch = h.match(/^#admin\/events\/edit\/(\d+)$/);
  if (editMatch) return { name: 'admin-event-edit', eventId: Number(editMatch[1]) };
  // Task #44: printable attendance report. Keyed by event_id; $-anchored and
  // disjoint from the edit regex above.
  const attendanceReportMatch = h.match(/^#admin\/events\/(\d+)\/attendance-report$/);
  if (attendanceReportMatch) return { name: 'admin-event-attendance-report', eventId: Number(attendanceReportMatch[1]) };
  if (h === '#admin/registrations') return { name: 'admin-section', section: 'registrations' };
  if (h === '#admin/series')        return { name: 'admin-section', section: 'series' };
  if (h === '#admin/presenters')    return { name: 'admin-section', section: 'presenters' };
  if (h === '#admin/locations')     return { name: 'admin-section', section: 'locations' };
  if (h === '#admin/resources')     return { name: 'admin-section', section: 'resources' };
  if (h === '#admin/tags')          return { name: 'admin-section', section: 'tags' };
  if (h === '#admin/reports')       return { name: 'admin-section', section: 'reports' };
  if (h === '#admin/subscribers')   return { name: 'admin-section', section: 'subscribers' };
  if (h === '#admin/pages')         return { name: 'admin-section', section: 'pages' };
  if (h === '#admin/farmers')       return { name: 'admin-section', section: 'farmers' };
  if (h === '#admin/farms')         return { name: 'admin-section', section: 'farms' };
  const farmDetailMatch = h.match(/^#admin\/farms\/(\d+)$/);
  if (farmDetailMatch) return { name: 'admin-farm-detail', farmId: Number(farmDetailMatch[1]) };
  if (h === '#admin/documents')     return { name: 'admin-section', section: 'documents' };
  const docDetailMatch = h.match(/^#admin\/documents\/([0-9a-f-]{36})$/i);
  if (docDetailMatch) return { name: 'admin-document-detail', documentId: docDetailMatch[1] };
  // Build S3 (Task #19): signature envelopes + agreements.
  if (h === '#admin/envelopes')     return { name: 'admin-section', section: 'envelopes' };
  const envelopeDetailMatch = h.match(/^#admin\/envelopes\/([0-9a-f-]{36})$/i);
  if (envelopeDetailMatch) return { name: 'admin-envelope-detail', envelopeId: envelopeDetailMatch[1] };
  if (h === '#admin/agreements')    return { name: 'admin-section', section: 'agreements' };
  const agreementDetailMatch = h.match(/^#admin\/agreements\/([0-9a-f-]{36})$/i);
  if (agreementDetailMatch) return { name: 'admin-agreement-detail', agreementId: agreementDetailMatch[1] };
  // Build S4 (Task #25): programs + enrollments.
  if (h === '#admin/programs')      return { name: 'admin-section', section: 'programs' };
  if (h === '#admin/enrollments')   return { name: 'admin-section', section: 'enrollments' };
  const enrollmentDetailMatch = h.match(/^#admin\/enrollments\/([0-9a-f-]{36})$/i);
  if (enrollmentDetailMatch) return { name: 'admin-enrollment-detail', enrollmentId: enrollmentDetailMatch[1] };
  // Build S6b (Task #37): record-external surfaces. Batch is a literal (no id);
  // the per-producer route carries the enrollment uuid. Both are disjoint from
  // the $-anchored detail regex above.
  if (h === '#admin/enrollments/record-external-batch') return { name: 'admin-enrollment-record-external-batch' };
  const enrollRecordExternalMatch = h.match(/^#admin\/enrollments\/([0-9a-f-]{36})\/record-external$/i);
  if (enrollRecordExternalMatch) return { name: 'admin-enrollment-record-external', enrollmentId: enrollRecordExternalMatch[1] };
  // Build S5 (Task #22): tracks + milestones.
  if (h === '#admin/tracks')        return { name: 'admin-section', section: 'tracks' };
  const trackDetailMatch = h.match(/^#admin\/tracks\/([0-9a-f-]{36})$/i);
  if (trackDetailMatch) return { name: 'admin-track-detail', trackPlanId: trackDetailMatch[1] };
  if (h === '#admin/milestonedefs') return { name: 'admin-section', section: 'milestonedefs' };
  // Build S6 (Task #26): practices catalog + CPA-52 submissions.
  if (h === '#admin/practices')     return { name: 'admin-section', section: 'practices' };
  if (h === '#admin/cpa52')         return { name: 'admin-section', section: 'cpa52' };
  if (h === '#admin/pages/new')     return { name: 'admin-pages-edit', pageId: 'new' };
  const pagesEditMatch = h.match(/^#admin\/pages\/(\d+)$/);
  if (pagesEditMatch)               return { name: 'admin-pages-edit', pageId: Number(pagesEditMatch[1]) };
  if (h === '#admin/bookings')      return { name: 'admin-section', section: 'bookings' };
  if (h === '#admin/budgets')       return { name: 'admin-section', section: 'budgets' };
  const budgetMatch = h.match(/^#admin\/budgets\/(\d+)$/);
  if (budgetMatch) return { name: 'admin-budget', calendarEntryId: Number(budgetMatch[1]) };

  // Event detail
  const eventMatch = h.match(/^#event\/(\d+)$/);
  if (eventMatch) return { name: 'event-detail', eventId: Number(eventMatch[1]) };
  const slugMatch = h.match(/^#e\/([a-z0-9-]+)$/);
  if (slugMatch) return { name: 'event-detail-slug', slug: slugMatch[1] };

  // Public attendance confirmation by UUID token (Task #48).
  const confirmHashMatch = h.match(/^#confirm\/([0-9a-f-]{36})$/i);
  if (confirmHashMatch) return { name: 'confirm', token: confirmHashMatch[1] };

  // Public day-of sign-in page (Task #52). URL: #signin/<event_id>/<day_index>.
  const signinMatch = h.match(/^#signin\/(\d+)\/(\d+)$/);
  if (signinMatch) {
    return { name: 'signin', eventId: Number(signinMatch[1]), dayIndex: Number(signinMatch[2]) };
  }

  // Task #2: UIF password reset landing. URL: #password-reset/<token>.
  const resetMatch = h.match(/^#password-reset\/([a-f0-9]{8,})$/i);
  if (resetMatch) return { name: 'password-reset', token: resetMatch[1] };

  // Task #9: email-subscription confirm + unsubscribe landings.
  const subConfirmMatch = h.match(/^#subscribe-confirm\/([a-f0-9]{8,})$/i);
  if (subConfirmMatch) return { name: 'subscribe-confirm', token: subConfirmMatch[1] };
  const unsubMatch = h.match(/^#unsubscribe\/([a-f0-9-]{8,})$/i);
  if (unsubMatch) return { name: 'unsubscribe', token: unsubMatch[1] };

  // Task #15: magic-link "Join meeting" landing. Token is the attendee
  // global_id (UUID format).
  const joinMatch = h.match(/^#join\/([0-9a-f-]{8,})$/i);
  if (joinMatch) return { name: 'join-meeting', token: joinMatch[1] };

  // Task #10: farmer auth pages + signed-in landing.
  if (h === '#farmer-signin')      return { name: 'farmer-signin' };
  if (h === '#my')                 return { name: 'farmer-home' };
  if (h === '#my/registrations')   return { name: 'farmer-registrations' };
  if (h === '#my/documents')       return { name: 'farmer-documents' };
  if (h === '#my/signatures')      return { name: 'farmer-signatures' };
  if (h === '#my/enrollment')      return { name: 'farmer-enrollment' };
  if (h === '#my/tracks')          return { name: 'farmer-tracks' };
  // Build S3: external magic-link signing (public, no portal chrome).
  const externalSignMatch = h.match(/^#sign\/([A-Za-z0-9_-]{43})$/);
  if (externalSignMatch) return { name: 'external-sign', linkToken: externalSignMatch[1] };
  // Task #44 Phase B: public tokenized stipend-address form. Same 43-char
  // base64url shape as #sign; the fragment is never sent to any server.
  const attendeeInfoMatch = h.match(/^#attendee-info\/([A-Za-z0-9_-]{43})$/);
  if (attendeeInfoMatch) return { name: 'attendee-info', token: attendeeInfoMatch[1] };
  const farmerVerifyMatch = h.match(/^#farmer-verify\/([a-f0-9]{8,})$/i);
  if (farmerVerifyMatch) return { name: 'farmer-verify', token: farmerVerifyMatch[1] };
  const farmerResetMatch = h.match(/^#farmer-reset\/([a-f0-9]{8,})$/i);
  if (farmerResetMatch) return { name: 'farmer-reset', token: farmerResetMatch[1] };
  // Task #16: invited producer sets first password.
  const farmerOnboardMatch = h.match(/^#farmer-onboard\/([a-f0-9]{8,})$/i);
  if (farmerOnboardMatch) return { name: 'farmer-onboard', token: farmerOnboardMatch[1] };

  return { name: 'home' };
}

export function App() {
  const { token, user, logout } = useAuth();
  const { farmer: farmerAuth } = useFarmerAuth();
  const [route, setRoute] = useState<Route>(getRoute());
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [showLandingQr, setShowLandingQr] = useState(false);
  const [registrationsBadge, setRegistrationsBadge] = useState<number | undefined>(undefined);

  useEffect(() => {
    const onHashChange = () => setRoute(getRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.hash = '';
  };

  // ---- Event detail page (any role, anonymous OK if event is public) ----
  if (route.name === 'event-detail') {
    return <EventDetailPage eventId={route.eventId} />;
  }
  if (route.name === 'event-detail-slug') {
    return <EventDetailPage slug={route.slug} />;
  }

  // ---- Public attendance confirmation (Task #48) ----
  if (route.name === 'confirm') {
    return <ConfirmPage token={route.token} />;
  }

  // ---- Public day-of sign-in (Task #52) ----
  if (route.name === 'signin') {
    return <SignInPage eventId={route.eventId} dayIndex={route.dayIndex} />;
  }

  // ---- Public password reset landing (Task #2) ----
  if (route.name === 'password-reset') {
    return <PasswordResetPage token={route.token} />;
  }

  // ---- Magic-link "Join meeting" landing (Task #15) ----
  if (route.name === 'join-meeting') {
    return <JoinMeetingPage token={route.token} />;
  }

  // ---- Public email-subscription confirm + unsubscribe landings (Task #9) ----
  if (route.name === 'subscribe-confirm') {
    return <SubscribeConfirmPage token={route.token} />;
  }
  if (route.name === 'unsubscribe') {
    return <UnsubscribePage token={route.token} />;
  }

  // ---- Farmer auth pages + signed-in landing (Task #10) ----
  if (route.name === 'farmer-signin') {
    return <FarmerSignInPage />;
  }
  if (route.name === 'farmer-verify') {
    return <FarmerVerifyPage token={route.token} />;
  }
  if (route.name === 'farmer-reset') {
    return <FarmerResetPage token={route.token} />;
  }
  if (route.name === 'farmer-onboard') {
    return <FarmerOnboardPage token={route.token} />;
  }
  if (route.name === 'farmer-home') {
    return <FarmerHomePage />;
  }
  if (route.name === 'farmer-registrations') {
    return <FarmerRegistrationsPage />;
  }

  // ---- Login: if already authed, send to role-appropriate landing ----
  if (route.name === 'login') {
    if (token && user) {
      if (user.role === 'uif')      window.location.hash = '#admin/dashboard';
      else if (user.role === 'agc') window.location.hash = '#agc';
      else                          window.location.hash = '#amp-events';
      return null;
    }
    return <LoginPage onCancel={() => (window.location.hash = '')} />;
  }

  // ---- Admin event create / edit — wrapped in AdminLayout shell.
  // EventForm now renders embedded (no own min-h-screen / header) so the
  // sidebar persists alongside it (Task #6).
  if (route.name === 'admin-event-create') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="events" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <EventForm />
      </AdminLayout>
    );
  }
  if (route.name === 'admin-event-bulk') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return <CsvBulkUpload />;
  }
  if (route.name === 'admin-event-edit') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="events" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <EventForm eventId={route.eventId} />
      </AdminLayout>
    );
  }

  // ---- Pages edit (new or existing) — Task #13 ----
  if (route.name === 'admin-pages-edit') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="pages" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminPagesPanel token={token} editingPageId={route.pageId} />
      </AdminLayout>
    );
  }

  // Build S2 (Task #18): per-document detail view — sidebar pinned to
  // the Documents section.
  if (route.name === 'admin-document-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="documents" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminDocumentsPanel token={token} documentId={route.documentId} />
      </AdminLayout>
    );
  }

  // Build S2 (Task #18): producer documents tab.
  if (route.name === 'farmer-documents') {
    return <FarmerDocumentsPage />;
  }

  // Build S3 (Task #19): producer signing surface + external magic link.
  if (route.name === 'farmer-signatures') {
    return <FarmerSignaturesTab />;
  }
  // Build S4 (Task #25): producer enrollment tab.
  if (route.name === 'farmer-enrollment') {
    return <FarmerEnrollmentTab />;
  }
  if (route.name === 'farmer-tracks') {
    return <FarmerTracksTab />;
  }
  if (route.name === 'attendee-info') {
    return <PublicAttendeeInfoPanel token={route.token} />;
  }
  if (route.name === 'external-sign') {
    return <ExternalSignPage linkToken={route.linkToken} />;
  }
  if (route.name === 'admin-envelope-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="envelopes" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminEnvelopesPanel token={token} envelopeId={route.envelopeId} />
      </AdminLayout>
    );
  }
  if (route.name === 'admin-agreement-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="agreements" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminAgreementsPanel token={token} agreementId={route.agreementId} />
      </AdminLayout>
    );
  }
  // Build S4 (Task #25): enrollment detail — sidebar pinned to Enrollments.
  if (route.name === 'admin-enrollment-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="enrollments" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminEnrollmentsPanel token={token} enrollmentId={route.enrollmentId} />
      </AdminLayout>
    );
  }
  // Build S6b (Task #37): record-external (per-producer) — sidebar pinned to Enrollments.
  if (route.name === 'admin-enrollment-record-external') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="enrollments" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminEnrollmentRecordExternalPanel token={token} enrollmentId={route.enrollmentId} />
      </AdminLayout>
    );
  }
  // Build S6b (Task #37): record-external batch wave — sidebar pinned to Enrollments.
  if (route.name === 'admin-enrollment-record-external-batch') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="enrollments" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminEnrollmentRecordExternalBatchPanel token={token} />
      </AdminLayout>
    );
  }
  // Build S5 (Task #22): track detail — sidebar pinned to Track plans.
  if (route.name === 'admin-track-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="tracks" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminTracksPanel token={token} trackPlanId={route.trackPlanId} />
      </AdminLayout>
    );
  }

  // Build S1 (Task #21): per-farm detail view (members + FSA chain +
  // subfields) — sidebar pinned to the Farms section.
  if (route.name === 'admin-farm-detail') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout current="farms" user={user} onSignOut={handleLogout} registrationsBadge={registrationsBadge}>
        <AdminFarmsPanel token={token} farmId={route.farmId} />
      </AdminLayout>
    );
  }

  // Per-event budget editor — wrapped in AdminLayout shell with the
  // sidebar pinned to the Budgets section.
  if (route.name === 'admin-budget') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return (
      <AdminLayout
        current="budgets"
        user={user}
        onSignOut={handleLogout}
        registrationsBadge={registrationsBadge}
      >
        <AdminBudgetPanel calendarEntryId={route.calendarEntryId} token={token} />
      </AdminLayout>
    );
  }

  // Printable attendance report (Task #44). Deliberately NOT wrapped in
  // AdminLayout — the layout's sidebar and top bar have no print: classes, so
  // wrapping this would print the admin nav. Bare-return precedent: CsvBulkUpload.
  if (route.name === 'admin-event-attendance-report') {
    if (!token || !user) { window.location.hash = '#login'; return null; }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }
    return <AdminEventAttendanceReportPanel token={token} eventId={route.eventId} />;
  }

  // ---- Admin section routes — wrapped in AdminLayout shell ----
  if (route.name === 'admin-section') {
    if (!token || !user) {
      window.location.hash = '#login';
      return null;
    }
    if (user.role !== 'uif') {
      window.location.hash = user.role === 'agc' ? '#agc' : '#amp-events';
      return null;
    }

    let inner: React.ReactNode = null;
    switch (route.section) {
      case 'dashboard':
        inner = <AdminDashboard token={token} onRegistrationsCount={setRegistrationsBadge} />;
        break;
      case 'events':
        inner = <AdminEventsPanel token={token} />;
        break;
      case 'registrations':
        inner = <AdminRegistrations token={token} onLogout={handleLogout} onRegistrationsCount={setRegistrationsBadge} />;
        break;
      case 'series':
        inner = <AdminSeriesPanel token={token} />;
        break;
      case 'presenters':
        inner = <AdminPresentersPanel token={token} />;
        break;
      case 'locations':
        inner = <AdminLocationsPanel token={token} />;
        break;
      case 'resources':
        inner = <AdminResourcesPanel token={token} />;
        break;
      case 'bookings':
        inner = <AdminBookingsPanel />;
        break;
      case 'budgets':
        inner = <AdminBudgetList token={token} />;
        break;
      case 'tags':
        inner = <AdminTagsPanel token={token} />;
        break;
      case 'reports':
        inner = <AdminReportsPanel token={token} />;
        break;
      case 'subscribers':
        inner = <AdminSubscribersPanel token={token} />;
        break;
      case 'pages':
        inner = <AdminPagesPanel token={token} />;
        break;
      case 'farmers':
        inner = <AdminFarmersPanel token={token} />;
        break;
      case 'farms':
        inner = <AdminFarmsPanel token={token} />;
        break;
      case 'documents':
        inner = <AdminDocumentsPanel token={token} />;
        break;
      case 'envelopes':
        inner = <AdminEnvelopesPanel token={token} />;
        break;
      case 'agreements':
        inner = <AdminAgreementsPanel token={token} />;
        break;
      case 'programs':
        inner = <AdminProgramsPanel token={token} />;
        break;
      case 'enrollments':
        inner = <AdminEnrollmentsPanel token={token} />;
        break;
      case 'tracks':
        inner = <AdminTracksPanel token={token} />;
        break;
      case 'milestonedefs':
        inner = <AdminMilestoneDefsPanel token={token} />;
        break;
      case 'practices':
        inner = <AdminPracticesPanel token={token} />;
        break;
      case 'cpa52':
        inner = <AdminCpa52Panel token={token} />;
        break;
      case 'settings':
        inner = <ComingSoon title={labelForSection(route.section)} />;
        break;
    }

    return (
      <AdminLayout
        current={route.section}
        user={user}
        onSignOut={handleLogout}
        registrationsBadge={registrationsBadge}
      >
        {inner}
      </AdminLayout>
    );
  }

  // ---- AGC shared room calendar (AGC + UIF) ----
  if (route.name === 'agc') {
    if (!token || !user) {
      window.location.hash = '#login';
      return null;
    }
    if (user.role !== 'agc' && user.role !== 'uif') {
      window.location.hash = '#amp-events';
      return null;
    }
    return <AgcCalendar />;
  }

  // ---- AMP read-only placeholder ----
  if (route.name === 'amp-events') {
    if (!token || !user) {
      window.location.hash = '#login';
      return null;
    }
    return <AmpCalendarPlaceholder />;
  }

  // ---- AMP marketing landing ----
  if (route.name === 'amp') {
    return <AmpFunding />;
  }

  // ---- Default: public homepage ----
  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      <header className="border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-14 md:h-16 w-auto" />
            <span className="sr-only">Up in Farms</span>
          </a>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowLandingQr(true)}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-uif-green"
              title="QR code for this page"
            >
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">QR code</span>
            </button>
            <a
              href="mailto:support@upinfarms.com"
              className="hidden sm:inline text-sm text-gray-600 hover:text-uif-green"
            >
              support@upinfarms.com
            </a>
            {user ? (
              <a
                href={
                  user.role === 'uif' ? '#admin/dashboard'
                  : user.role === 'agc' ? '#agc'
                  : '#amp-events'
                }
                className="text-sm font-medium text-uif-green hover:text-uif-green-dark"
              >
                My dashboard ({user.role.toUpperCase()})
              </a>
            ) : farmerAuth ? (
              <a
                href="#my"
                className="text-sm font-medium text-uif-green hover:text-uif-green-dark"
              >
                My account
              </a>
            ) : (
              <a
                href="#farmer-signin"
                className="text-sm font-medium text-uif-green hover:text-uif-green-dark"
              >
                Producer sign in
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <CalendarHero />

        <section className="max-w-5xl mx-auto px-4 pt-10">
          <AmpAlert />
        </section>

        <section className="max-w-5xl mx-auto px-4 pt-8">
          <PublicCalendar />
        </section>

        <section className="max-w-5xl mx-auto px-4 pt-8 pb-8">
          <TagFilter selectedTags={filterTags} onChange={setFilterTags} />
          <PublicCalendar
            initialView="listYear"
            showToolbar={false}
            includePast={false}
            caption="Upcoming events"
            filterTags={filterTags}
          />
        </section>

        {/* Task #9: email subscription opt-in. Lives below the calendar so
            visitors can see what's coming up first, then sign up to be
            notified of new ones. */}
        <section className="max-w-3xl mx-auto px-4 pb-12">
          <SubscribeBox source="homepage" />
        </section>
      </main>

      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-8 text-center text-sm text-gray-500 space-y-2">
          <p>
            Up in Farms LLC · 2093 Lakeland Dr, Jackson, MS 39216 ·{' '}
            <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">
              support@upinfarms.com
            </a>
          </p>
          <p className="text-xs text-gray-400">
            This site is supported in part by the USDA Local Food Promotion Program (LFPP).
          </p>
          <p className="text-[11px] text-gray-400">
            <a href="#login" className="hover:text-uif-green hover:underline">Admin</a>
          </p>
        </div>
      </footer>

      <QrCodeModal
        open={showLandingQr}
        onClose={() => setShowLandingQr(false)}
        url={window.location.origin + '/'}
        title="QR code · UIF events landing"
        filename="uif-events-landing"
      />
    </div>
  );
}

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
      <p className="text-sm text-gray-600">This section is part of Phase 2. Coming soon.</p>
    </div>
  );
}

function labelForSection(s: AdminSection): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
